import React, { FunctionComponent, useEffect, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { dispatch } from '@Redux/store';
import { useCart } from '@Utils/context/CartContext';
import useTranslate from '@Utils/hooks/useTranslate';
import { EMPTY_OUTLET, useOutlet } from '@Utils/context/OutletContext';
import { useBuyNowPayLater } from '@Utils/context/BuyNowPayLaterContext';
import { modalTypes, setModal } from '@Redux/modules/modal/actions';
import Card from '@Components/Card/Card';
import { ISegmentSize } from '@Components/controls/Segment/Segment';
import SegmentSelector from '@Components/SegmentSelector/SegmentSelector';
import ConditionalRender from '@Components/ConditionalRender/ConditionalRender';
import { ItemType, ProductsService } from '@Services/productsService/productsService';
import { isPrepaidIncluded } from './util';
import Title from './components/Title';
import AddCTA from './components/AddCTA';
import HomeDelivery from './components/HomeDelivery';
import { CardContainer, ContentWrapper } from '@Components/Card/cards/ShippingDetails/ShippingDetails.styled';
import useAddressBook from '@Utils/hooks/useAddressBook';
import { setActiveAddressBook } from '@Redux/modules/addressBook/actions';

export enum DeliveryType {
	HOME_DELIVERY = 'HOME_DELIVERY',
	OUTLET_PICKUP = 'OUTLET_PICKUP',
}

const ShippingDetails: FunctionComponent<types.cards.IShippingDetailsCardProps> = ({
	params: { contactDetails, isSimplifiedCheckout = false },
}) => {
	const { addressBook, selectedAddress, getTitle, updateShippingAddress } = useAddressBook();
	const newAddressBook = useSelector((state: types.redux.IState) => state.addressBook.newAddressBook);
	const { cart } = useCart();
	const { translate } = useTranslate();
	const { isBuyNowPayLaterInCart } = useBuyNowPayLater();
	const { selectedOutlet, availableOutlets, setSelectedOutlet } = useOutlet();
	const pickupDelivery = useSelector((state: types.redux.IState) => state.checkout.pickup);

	const [hasGiftCards, setHasGiftCards] = useState<boolean>(false);
	const [hasDevice, setHasDevice] = useState<boolean>(false);
	const [launchDate, setLaunchDate] = useState<string>('');
	const [deliveryType, setDeliveryType] = useState<DeliveryType>(DeliveryType.HOME_DELIVERY);
	const orderIncludesPrepaid = isPrepaidIncluded(cart.items);
	const pickUpOutlet = !!selectedOutlet.branchId;
	const outletpickupDisabled =
		availableOutlets.filter((item) => {
			return item.available === true;
		}).length === 0 || isBuyNowPayLaterInCart;

	useEffect(() => {
		if (!outletpickupDisabled) {
			setDeliveryType(DeliveryType.OUTLET_PICKUP);
		}
	}, [outletpickupDisabled]);

	useEffect(() => {
		cart.items.map((cartItem) => {
			ProductsService.getProductById({ productId: cartItem.productId, itemType: ItemType.UNKNOWN }).subscribe(
				({ item }) => {
					if (item?.isGiftCard || item?.isDigitalProduct) {
						setHasGiftCards(true);
					} else if (item && !item?.isDigitalProduct) {
						setHasDevice(true);
					}
					if (pickupDelivery) {
						setLaunchDate(moment(item.launchDate).format('DD MMM YYYY'));
					}
				},
				() => {}
			);
		});
	}, [cart]);

	const setSelectedDeliveryType = (method: DeliveryType) => {
		if (method === DeliveryType.HOME_DELIVERY) {
			setSelectedOutlet(EMPTY_OUTLET);
		} else if (method === DeliveryType.OUTLET_PICKUP) {
			dispatch(
				setActiveAddressBook({
					activeAddressBook: undefined,
				})
			);
			updateShippingAddress(undefined);
		}
		setDeliveryType(method);
	};

	useEffect(() => {
		if (addressBook.length === 1 && deliveryType === DeliveryType.HOME_DELIVERY) {
			updateShippingAddress(addressBook[0]);
			dispatch(
				setActiveAddressBook({
					activeAddressBook: addressBook[0].addressId,
				})
			);
		} else {
			addressBook.forEach((_address) => {
				if (newAddressBook === _address.addressId) {
					dispatch(
						setActiveAddressBook({
							activeAddressBook: _address.addressId,
						})
					);
				}
			});
		}
	}, [addressBook, deliveryType]);

	const addNewAddress = () => {
		dispatch(
			setModal({
				type: modalTypes.DELIVERY_ADDRESS,
				data: { type: 'shipmentAddress', action: 'create', skipMap: true },
				withoutContainer: true,
			})
		);

		return false;
	};

	return (
		<CardContainer>
			<Title
				showEdit={isSimplifiedCheckout && addressBook.length > 0 && deliveryType === DeliveryType.HOME_DELIVERY}
				showOrder={orderIncludesPrepaid || pickUpOutlet}
				isSimplifiedCheckout={isSimplifiedCheckout}
			/>
			<ContentWrapper>
				<ConditionalRender
					show={isSimplifiedCheckout}
					onTrue={() => (
						<>
							<SegmentSelector<DeliveryType>
								show={true}
								setMethod={setSelectedDeliveryType}
								leftSegmentName={translate('secure-checkout.delivery.home-delivery')}
								rightSegmentName={translate('secure-checkout.delivery.outlet-pickup')}
								rightDisabled={outletpickupDisabled}
								leftSegmentValue={DeliveryType.HOME_DELIVERY}
								rightSegmentValue={DeliveryType.OUTLET_PICKUP}
								selectedValue={deliveryType}
								size={ISegmentSize.SMALL}
							/>
							<ConditionalRender
								show={deliveryType === DeliveryType.OUTLET_PICKUP}
								onTrue={() => <Card card="outletPickup" />}
							/>
							<ConditionalRender
								show={deliveryType === DeliveryType.HOME_DELIVERY}
								onTrue={() => (
									<>
										<HomeDelivery
											contactDetails={contactDetails}
											hasGiftCards={hasGiftCards}
											hasDevice={hasDevice}
											getTitle={getTitle}
											isSimplifiedCheckout={isSimplifiedCheckout}
											launchDate={launchDate}
											orderIncludesPrepaid={orderIncludesPrepaid}
											pickupDelivery={pickupDelivery}
											pickUpOutlet={pickUpOutlet}
											selectedAddress={selectedAddress}
										/>
										<AddCTA
											label={translate('secure-checkout.add-new-address')}
											onClick={addNewAddress}
											uniqueId="shippingDetails.addNewAddress"
										/>
									</>
								)}
							/>
						</>
					)}
					onFalse={() => (
						<HomeDelivery
							contactDetails={contactDetails}
							getTitle={getTitle}
							hasGiftCards={hasGiftCards}
							hasDevice={hasDevice}
							isSimplifiedCheckout={isSimplifiedCheckout}
							launchDate={launchDate}
							orderIncludesPrepaid={orderIncludesPrepaid}
							pickupDelivery={pickupDelivery}
							pickUpOutlet={pickUpOutlet}
							selectedAddress={selectedAddress}
						/>
					)}
				/>
			</ContentWrapper>
		</CardContainer>
	);
};

export default ShippingDetails;
